// SPDX-License-Identifier: AGPL-3.0-or-later
// SPDX-FileCopyrightText: 2026 Ben Richardson <hi@ben.gy>
//
// Pure derived views over the seat list: the Mackerras pendulum ordering, and
// the auto-detected insights. No DOM here so it is unit-testable.
import type { ElectionData, Seat } from './data.ts';

export interface PendulumSide {
  side: 'ALP' | 'COA';
  seats: Seat[]; // ordered most-marginal first (smallest TPP margin)
}

/**
 * Split every seat onto the Labor or Coalition side of the two-party-preferred
 * line and order each side most-marginal first. Every seat has a notional TPP,
 * so all 150 appear — crossbench-held seats sit on whichever side their notional
 * TPP favours (the view colours them by who actually won).
 */
export function pendulum(seats: Seat[]): { alp: PendulumSide; coa: PendulumSide } {
  const alp = seats.filter((s) => s.tppLeader === 'ALP').sort((a, b) => a.tppMargin - b.tppMargin);
  const coa = seats.filter((s) => s.tppLeader === 'COA').sort((a, b) => a.tppMargin - b.tppMargin);
  return { alp: { side: 'ALP', seats: alp }, coa: { side: 'COA', seats: coa } };
}

/** Seats where a uniform national swing of `pct` to Labor would cross 50% TPP. */
export function seatsFlippedBy(seats: Seat[], swingToALP: number): Seat[] {
  return seats.filter((s) => {
    const newALP = s.tppALPPct + swingToALP;
    const wasALP = s.tppALPPct >= 50;
    const nowALP = newALP >= 50;
    return wasALP !== nowALP;
  });
}

export interface Insight {
  severity: 'info' | 'good' | 'warn' | 'alert';
  title: string;
  big?: string;
  body: string;
  seat?: string;
}

export function buildInsights(data: ElectionData): Insight[] {
  const s = data.seats;
  const n = data.national;
  const out: Insight[] = [];

  const byMargin = [...s].sort((a, b) => a.marginPct - b.marginPct);
  const closest = byMargin[0];
  out.push({
    severity: 'alert',
    title: 'The closest seat in the country',
    big: `${closest.marginPct >= 0.1 ? closest.marginPct.toFixed(1) : closest.marginPct.toFixed(2)}%`,
    body: `${closest.name} (${closest.state}) was decided by ${Math.round(Math.abs(closest.tcp[0].votes - closest.tcp[1].votes)).toLocaleString('en-AU')} votes — ${closest.tcp[0].surname.replace(/\b\w/g, (c) => c.toUpperCase())} over ${closest.tcp[1].surname.replace(/\b\w/g, (c) => c.toUpperCase())}.`,
    seat: closest.name,
  });

  const changed = s.filter((x) => x.changedHands);
  out.push({
    severity: 'info',
    title: 'Seats that changed hands',
    big: String(changed.length),
    body: `${changed.length} electorates were won by a different party than in 2022 — the churn behind the headline result.`,
  });

  const dutton = s.find((x) => x.name === 'Dickson');
  if (dutton && dutton.changedHands) {
    out.push({
      severity: 'alert',
      title: 'An opposition leader lost his seat',
      body: `Dickson (QLD) fell from ${dutton.prevGroup === 'COA' ? 'the Coalition' : dutton.prevParty} to Labor${dutton.tppSwingToALP != null ? ` on a ${dutton.tppSwingToALP.toFixed(1)}-point swing` : ''} — Peter Dutton became the first federal opposition leader to lose his own electorate at a general election.`,
      seat: 'Dickson',
    });
  }

  const teal = s.filter((x) => x.member.group === 'IND' && x.tppLeader === 'COA');
  out.push({
    severity: 'good',
    title: '“Teal” and other independents held the Coalition',
    big: String(s.filter((x) => x.member.group === 'IND').length),
    body: `${s.filter((x) => x.member.group === 'IND').length} independents were elected; ${teal.length} of them hold a seat that still leans Coalition on the notional two-party count — Liberal on paper, independent in the parliament.`,
  });

  const swingSeats = s.filter((x): x is Seat & { tppSwingToALP: number } => x.tppSwingToALP != null);
  const bigSwing = [...swingSeats].sort((a, b) => b.tppSwingToALP - a.tppSwingToALP)[0];
  out.push({
    severity: 'info',
    title: 'The biggest swing to Labor',
    big: `+${bigSwing.tppSwingToALP.toFixed(1)}`,
    body: `${bigSwing.name} (${bigSwing.state}) recorded the largest two-party swing towards Labor of any comparable seat.`,
    seat: bigSwing.name,
  });

  const againstSwing = [...swingSeats].sort((a, b) => a.tppSwingToALP - b.tppSwingToALP)[0];
  if (againstSwing.tppSwingToALP < 0) {
    out.push({
      severity: 'warn',
      title: 'The biggest swing against Labor',
      big: `${againstSwing.tppSwingToALP.toFixed(1)}`,
      body: `Not every seat moved the same way — ${againstSwing.name} (${againstSwing.state}) swung ${Math.abs(againstSwing.tppSwingToALP).toFixed(1)} points to the Coalition.`,
      seat: againstSwing.name,
    });
  }

  const nonClassic = s.filter((x) => x.nonClassic);
  out.push({
    severity: 'info',
    title: 'Not every seat was Labor v Coalition',
    big: String(nonClassic.length),
    body: `${nonClassic.length} seats were “non-classic” — the final two candidates included a Green or an independent, so the notional two-party line did not decide them.`,
  });

  const majorPrimary = data.parties
    .filter((p) => p.ab === 'ALP' || p.group === 'COA')
    .reduce((a, p) => a + p.pct, 0);
  out.push({
    severity: 'warn',
    title: 'A record-low major-party vote',
    big: `${majorPrimary.toFixed(1)}%`,
    body: `Labor and the Coalition together took barely two-thirds of first preferences; one in three voters started with someone else.`,
  });

  const safest = byMargin[byMargin.length - 1];
  out.push({
    severity: 'good',
    title: 'The safest seat in the country',
    big: `${safest.marginPct.toFixed(1)}%`,
    body: `${safest.name} (${safest.state}) is held by ${safest.member.group === 'ALP' ? 'Labor' : safest.member.group === 'COA' ? 'the Coalition' : safest.member.partyName} with the largest two-candidate margin nationally.`,
    seat: safest.name,
  });

  const worstInformal = [...s].sort((a, b) => b.informalPct - a.informalPct)[0];
  out.push({
    severity: 'warn',
    title: 'Highest informal vote',
    big: `${worstInformal.informalPct.toFixed(1)}%`,
    body: `${worstInformal.name} (${worstInformal.state}) had the highest share of ballots that could not be counted — against a national ${n.informalPct.toFixed(1)}%.`,
    seat: worstInformal.name,
  });

  return out;
}
